// helpers/postArchive.js
//
// Single source of truth for the "Archive" behaviour shared by the three
// post views (Calendar / List / Kanban) on AllPostsPage + PostsPagePersonalArea.
// Modelled 1:1 on the Self-Construction board's archive (pages/SelfConstruct):
//
//   - Archived posts are HIDDEN by default. A "Show archived (N)" toggle
//     reveals them; when revealed the archived shelf REPLACES the working
//     board so it isn't squeezed beside the live columns.
//   - Every post carries a per-card button. On a live post it archives it
//     (queueStatus → 'archived'); on an archived post the SAME button sends
//     it back to the queue (queueStatus → 'awaiting-approval').
//   - Failed / Posted / Rejected posts auto-archive once they are older than
//     24h, so the working board only ever shows live work. The backend
//     scheduler (prigmar-worker postArchiveScheduler) persists this; the
//     `shouldAutoArchive` rule here makes the UI reflect it immediately,
//     before the next sweep runs.
//
// Pure (no redux, no API) + CommonJS so it runs under bare `npx jest` the
// same way helpers/kanbanBucket.js and helpers/deriveQueueStatus.js do.
//
// Sources of truth:
//   - Post.queueStatus enum            back/models/post.js
//   - POST_KANBAN_STATUSES             frontend/src/constants/kanbanStatuses.js
//   - SelfConstruct.archiveControl     frontend/src/pages/SelfConstruct/SelfConstruct.jsx

// The lifecycle string an archived post carries, and where the un-archive
// button sends a post back to (the head of the approval queue).
const ARCHIVED_STATUS = 'archived';
const UNARCHIVE_STATUS = 'awaiting-approval';

// Terminal lifecycle states that auto-archive once they age out: a post
// that's been Posted, has Failed, or was Rejected is done — keeping it on
// the live board forever just buries the active work. After the window it
// moves to the Archive shelf (still reachable via "Show archived").
const AUTO_ARCHIVE_STATUSES = ['posted', 'posting-failed', 'rejected'];
const AUTO_ARCHIVE_AGE_MS = 24 * 60 * 60 * 1000; // 1 day / 24 hours

// Pull the lifecycle string off whichever field carries it — queueStatus
// preferred, then a plain status string, then a nested status object. Same
// priority order helpers/kanbanBucket.js + helpers/postDisplay.js use, so
// all surfaces agree on which field is the source of truth.
function rawStatusOf(item) {
  if (!item || typeof item !== 'object') return null;
  if (typeof item.queueStatus === 'string' && item.queueStatus) return item.queueStatus;
  if (typeof item.status === 'string' && item.status) return item.status;
  if (item.status && typeof item.status === 'object') {
    return item.status.key || item.status.name || null;
  }
  return null;
}

// Has this post been (explicitly) archived?
function isArchived(post) {
  return rawStatusOf(post) === ARCHIVED_STATUS;
}

// The per-card archive control. Mirrors SelfConstruct.archiveControl exactly:
// an archived post's button un-archives it back to the queue; any other
// post's button archives it. Pure + exported so the move-target + label
// can't silently drift from the self-construct board.
function archiveControl(post) {
  const archived = isArchived(post);
  return archived
    ? { archived: true, label: '♻', target: UNARCHIVE_STATUS, title: 'Un-archive — send back to awaiting approval' }
    : { archived: false, label: '📦', target: ARCHIVED_STATUS, title: 'Archive this post' };
}

// The timestamp we age a post from. Prefer the time it last changed
// (updated_at — set whenever the lifecycle transitions to posted/failed/
// rejected), then the scheduled time, then creation. Tolerant of the
// several date field shapes a post can carry.
function postTimestamp(post) {
  if (!post || typeof post !== 'object') return null;
  const candidates = [post.updated_at, post.updatedAt, post.postPostedDate, post.scheduled_at, post.created_at, post.createdAt];
  for (const c of candidates) {
    if (!c) continue;
    const t = new Date(c).getTime();
    if (!isNaN(t)) return t;
  }
  return null;
}

// Should this post auto-archive? True only for a terminal post (posted /
// failed / rejected) that is older than the 24h window. Explicitly
// archived posts return false here — they're already archived, handled by
// isArchived. `now` is injectable so the rule is deterministic in tests.
function shouldAutoArchive(post, now = new Date()) {
  if (!post) return false;
  const raw = rawStatusOf(post);
  if (!AUTO_ARCHIVE_STATUSES.includes(raw)) return false;
  const ts = postTimestamp(post);
  if (ts == null) return false;
  const nowMs = now instanceof Date ? now.getTime() : new Date(now).getTime();
  if (isNaN(nowMs)) return false;
  return nowMs - ts > AUTO_ARCHIVE_AGE_MS;
}

// The archive control for the Update Post modal header (CreatePostModal).
// The modal renders an Archive button just before the (X) close icon so a
// post opened for edit from ANY surface (Calendar / List / Kanban / brand
// tree) can be sent to the Archive shelf in one click. Returns null in
// Create mode (no selectedPost ⇒ nothing to archive, so no button); for an
// existing post it reuses archiveControl, so the label/target/title stay in
// lock-step with the per-card buttons (📦 Archive a live post, ♻ un-archive
// an archived one back to awaiting-approval).
function modalArchiveControl(selectedPost) {
  if (!selectedPost) return null;
  return archiveControl(selectedPost);
}

// The single archive action a per-card button performs, resolved for one
// post. Returns the queueStatus to PUT (`target`), whether the post was
// already archived, and the success toast text — so every page's
// handleArchive (AllPostsPage + PostsPagePersonalArea) sends the same enum
// and shows the same wording without each re-deriving it from archiveControl.
// Pure + exported so the target/message can't drift between surfaces.
function archiveActionFor(post) {
  const { archived, target } = archiveControl(post);
  return {
    archived,
    target,
    message: target === ARCHIVED_STATUS ? 'Post archived' : 'Post sent back to awaiting approval',
  };
}

// The label the "Show archived" toggle renders. Centralised here so every
// surface that integrates the toggle (AllPostsPage header dropdown +
// ContentFilterBar, and PostsPagePersonalArea's header dropdown) shows the
// exact same text — "📦 Show archived (N)" when hidden, "Hide archived" when
// the shelf is revealed. Pure so the wording can't drift between surfaces.
function archiveToggleLabel(showArchived, count = 0) {
  return showArchived ? 'Hide archived' : `📦 Show archived (${count})`;
}

// Split a post list into the live board vs the archive shelf, applying both
// the explicit `archived` status AND the 24h auto-archive rule. `visible`
// is what the current view should render: the shelf when `showArchived` is
// on, the live board otherwise — exactly how the self-construct board's
// boardColumns(showArchived) swaps the archived tab in for the working set.
function partitionPostsForArchive(posts, showArchived, now = new Date()) {
  const archived = [];
  const active = [];
  for (const p of Array.isArray(posts) ? posts : []) {
    if (isArchived(p) || shouldAutoArchive(p, now)) archived.push(p);
    else active.push(p);
  }
  return { archived, active, visible: showArchived ? archived : active };
}

module.exports = {
  ARCHIVED_STATUS,
  UNARCHIVE_STATUS,
  AUTO_ARCHIVE_STATUSES,
  AUTO_ARCHIVE_AGE_MS,
  rawStatusOf,
  isArchived,
  archiveControl,
  archiveActionFor,
  modalArchiveControl,
  postTimestamp,
  shouldAutoArchive,
  archiveToggleLabel,
  partitionPostsForArchive,
};
